import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import '../ReferralLanding.css';

function ReferralLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [referrerName, setReferrerName] = useState('');
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const refCode = searchParams.get('ref');

    if (!refCode) {
      // No referral code, redirect to the Event Series picker
      if (mixpanel && mixpanel.__loaded) {
        mixpanel.track('Referral Funnel: Landing Page Accessed Without Code', {
          funnel_step: 'invite_landing',
          funnel_step_number: 1,
          has_referral_code: false
        });
      }
      navigate('/events');
      return;
    }

    // Track arrival at referral landing page
    const upperCode = refCode.toUpperCase().trim();
    if (mixpanel && mixpanel.__loaded) {
      mixpanel.track('Referral Funnel: Landing Page Viewed', {
        funnel_step: 'invite_landing',
        funnel_step_number: 1,
        referral_code: upperCode,
        has_referral_code: true
      });
    }

    // Store referral code in localStorage
    localStorage.setItem('referralCode', upperCode);

    // Validate the referral code and get referrer name
    validateReferralCode(upperCode);
  }, [searchParams, navigate]);

  const validateReferralCode = async (code) => {
    try {
      const response = await fetch('/api/referral/validate-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referralCode: code })
      });

      const data = await response.json();

      if (data.valid) {
        setReferrerName(data.referrerName || 'Your friend');
        setIsValidating(false);

        // Track successful validation
        if (mixpanel && mixpanel.__loaded) {
          mixpanel.track('Referral Funnel: Code Validated Successfully', {
            funnel_step: 'invite_landing',
            funnel_step_number: 1,
            referral_code: code,
            referrer_name: data.referrerName,
            validation_status: 'success'
          });
        }
      } else {
        setError('This invite link is no longer valid.');
        setIsValidating(false);

        // Track validation failure
        if (mixpanel && mixpanel.__loaded) {
          mixpanel.track('Referral Funnel: Code Validation Failed', {
            funnel_step: 'invite_landing',
            funnel_step_number: 1,
            referral_code: code,
            validation_status: 'invalid_code',
            error_message: 'Invalid referral code'
          });
        }
      }
    } catch (err) {
      console.error('Error validating referral code:', err);
      setError('Unable to validate invite link. Please try again.');
      setIsValidating(false);

      // Track validation error
      if (mixpanel && mixpanel.__loaded) {
        mixpanel.track('Referral Funnel: Code Validation Error', {
          funnel_step: 'invite_landing',
          funnel_step_number: 1,
          referral_code: code,
          validation_status: 'error',
          error_message: err.message
        });
      }
    }
  };

  const handleContinue = () => {
    const refCode = searchParams.get('ref');

    // Track continue button click
    if (mixpanel && mixpanel.__loaded) {
      mixpanel.track('Referral Funnel: Continue Button Clicked', {
        funnel_step: 'invite_landing',
        funnel_step_number: 1,
        referral_code: refCode ? refCode.toUpperCase().trim() : null,
        referrer_name: referrerName,
        next_step: 'events'
      });
    }

    navigate('/events');
  };

  if (isValidating) {
    return (
      <div className="referral-landing">
        <div className="referral-landing-container">
          <div className="referral-landing-spinner">
            <div className="spinner"></div>
            <p>Validating your invite...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="referral-landing">
        <div className="referral-landing-container">
          <div className="referral-landing-error">
            <h1>Oops!</h1>
            <p>{error}</p>
            <button
              className="referral-landing-button"
              onClick={() => navigate('/events')}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="referral-landing">
      <div className="referral-landing-container">
        <div className="referral-landing-content">
          <div className="referral-landing-icon">
            🎉
          </div>

          <h1 className="referral-landing-title">
            You've Been Invited!
          </h1>

          <p className="referral-landing-subtitle">
            <strong>{referrerName}</strong> has invited you to join OkGather
          </p>

          <div className="referral-landing-benefits">
            <div className="referral-benefit-item">
              <span className="benefit-icon">✓</span>
              <span className="benefit-text">Get <strong>£10 off</strong> your first payment</span>
            </div>
            <div className="referral-benefit-item">
              <span className="benefit-icon">✓</span>
              <span className="benefit-text">Join meaningful gatherings in your area</span>
            </div>
            <div className="referral-benefit-item">
              <span className="benefit-icon">✓</span>
              <span className="benefit-text">Connect with like-minded people</span>
            </div>
            <div className="referral-benefit-item">
              <span className="benefit-icon">✓</span>
              <span className="benefit-text">Start with a <strong>30-day free trial</strong></span>
            </div>
          </div>

          <button
            className="referral-landing-button"
            onClick={handleContinue}
          >
            Continue
          </button>

          <p className="referral-landing-note">
            Sign up in seconds and start booking gatherings straight away
          </p>
        </div>
      </div>
    </div>
  );
}

export default ReferralLanding;
